import React, { useDeferredValue, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'

const StyleIconButton = styled(IconButton)({
  borderRadius: '8px',
  color: circlesOrbitColorPalette.primary.main,
  padding: '0px 16px',
  '&:hover': {
    backgroundColor: circlesOrbitColorPalette.neutrals[100]
  }
})

interface FilterProps {
  uniqueCompanies: string[]
  companiesChecked: string[]
  setCompaniesChecked: (value: string[]) => void
}
const FilterMenu = ({
  uniqueCompanies,
  setCompaniesChecked,
  companiesChecked
}: FilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [companySearch, setCompanySearch] = useState('')
  const deferredCompanySearchInput = useDeferredValue(companySearch)

  const handleCompanySearch = (e) => {
    setCompanySearch(e.target.value)
  }

  const handleCompanyCheckBox = (e) => {
    if (e.target.checked) {
      setCompaniesChecked([e.target.value, ...companiesChecked])
      setCompanySearch('')
    } else {
      const otherCompanies = companiesChecked.filter(
        (company) => company !== e.target.value
      )
      setCompanySearch('')
      setCompaniesChecked(otherCompanies)
    }
  }

  const searchCompanyList = useMemo(() => {
    return uniqueCompanies.filter((company) =>
      company.toLowerCase().includes(deferredCompanySearchInput.toLowerCase())
    )
  }, [deferredCompanySearchInput])

  return (
    <>
      <StyleIconButton onClick={handleFilterClick}>
        <FilterListIcon />
        <Typography sx={{ color: 'primary.main' }}>
          {' '}
          Filter({companiesChecked.length})
        </Typography>
      </StyleIconButton>

      <Paper sx={{ width: 400, maxWidth: '100%' }}>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <Box
            sx={{
              padding: '8px 16px',
              margin: '16px 8px'
            }}
          >
            <TextColorFontBody sx={{ fontWeight: 600, marginBottom: '8px' }}>
              Company
            </TextColorFontBody>
            <TextField
              value={deferredCompanySearchInput}
              onChange={(e) => handleCompanySearch(e)}
              variant='outlined'
              size='small'
              sx={{ width: '70%', marginBottom: '8px' }}
              placeholder='Search...'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Box sx={{ margin: '0 16px' }}>
              <FormControl>
                {searchCompanyList.map((company) => (
                  <FormControlLabel
                    label={
                      <TextColorFontBody sx={{ fontSize: '12px' }}>
                        {company}
                      </TextColorFontBody>
                    }
                    value={company}
                    key={company}
                    control={
                      <Checkbox
                        checked={companiesChecked.includes(company)}
                        value={company}
                        onChange={(e) => handleCompanyCheckBox(e)}
                        sx={{ color: 'neutrals.main' }}
                      />
                    }
                    labelPlacement='end'
                    sx={{ height: '32px' }}
                  />
                ))}
              </FormControl>
            </Box>
          </Box>
          <Divider sx={{ width: '90%', margin: 'auto' }} />
          <Box
            sx={{
              padding: '8px 16px',
              margin: '0px 8px'
            }}
          >
            <TextColorFontBody sx={{ fontWeight: 600, marginBottom: '8px' }}>
              Payment Status
            </TextColorFontBody>
            <Box sx={{ margin: '0 16px' }}>
              <FormControl>
                {['Paid', 'Due', 'Canceled'].map((status) => (
                  <FormControlLabel
                    sx={{ height: '32px' }}
                    key={status}
                    label={
                      <TextColorFontBody sx={{ fontSize: '12px' }}>
                        {status}
                      </TextColorFontBody>
                    }
                    control={
                      <Checkbox
                        sx={{ color: 'neutrals.main' }}
                        value={status}
                      />
                    }
                  />
                ))}
              </FormControl>
            </Box>
          </Box>
        </Menu>
      </Paper>
    </>
  )
}

export default FilterMenu
